"use client";

import { useState, useEffect } from "react";
import "./TeacherInsight.css"; // Import styling for the insight component
import { API_URL } from "../config";
// import {
//   Bar,
//   BarChart,
//   CartesianGrid,
//   Legend,
//   ResponsiveContainer,
//   Tooltip,
//   XAxis,
//   YAxis,
// } from "./ui/chart";

function TeacherInsight({ assessmentId, questionId }) {
  const [insightData, setInsightData] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError("");

        // Fetch both insight and analysis data
        const [insightResponse, analysisResponse] = await Promise.all([
          fetch(
            `${API_URL}/api/teacher-insight/${assessmentId}/${encodeURIComponent(
              questionId
            )}`
          ),
          fetch(
            `${API_URL}/api/question-analysis/${assessmentId}/${questionId}`
          ),
        ]);

        if (!insightResponse.ok) {
          if (insightResponse.status === 404) {
            setError("No student submissions found for this question yet.");
          } else {
            setError(
              `Error loading teacher insights: ${insightResponse.status}`
            );
          }
          return;
        }

        if (!analysisResponse.ok) {
          console.warn("Analysis data not available");
          // Continue with just insight data if analysis fails
        }

        const insightData = await insightResponse.json();
        setInsightData(insightData);

        if (analysisResponse.ok) {
          const analysisData = await analysisResponse.json();
          setAnalysisData(analysisData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Unable to load insights. Please try again later.");
      } finally {
        setLoading(false);
      }
    }

    if (assessmentId && questionId) {
      fetchData();
    }
  }, [assessmentId, questionId]);

  const renderSubmissionPattern = (pattern) => (
    <div className="submission-pattern" key={pattern.submission}>
      <div className="frequency">
        Frequency: {Number(pattern.frequency).toFixed(1)}% ({pattern.count}{" "}
        submissions)
      </div>
      <pre className="code-block">{pattern.submission}</pre>
      {pattern.feedback && (
        <div className="error-feedback">Error: {pattern.feedback}</div>
      )}
    </div>
  );

  if (loading) return <p>Loading insights...</p>;
  if (error) return <p className="error-message">{error}</p>;
  if (!insightData || !analysisData) return <p>No data available.</p>;

  return (
    <div className="teacher-insight">
      <h2>Aggregated Insights</h2>

      {/* Distribution Section */}
      <div className="distribution-section">
        <div className="status-chips">
          {Object.entries(analysisData.distribution || {}).map(
            ([status, count]) => (
              <span
                key={status}
                className={`status-chip ${status.toLowerCase()}`}
              >
                {status.replace(/_/g, " ")}: {count}
              </span>
            )
          )}
        </div>
      </div>

      {/* Main Insight */}
      <p className="insight-message">{insightData.teacher_insight}</p>
      <p>
        <strong>Total Submissions:</strong> {insightData.total_submissions}
      </p>

      {/* Common Patterns Section */}
      <div className="analysis-section">
        <div className="error-section compilation">
          <button
            className="accordion-button"
            onClick={(e) => e.target.parentElement.classList.toggle("active")}
          >
            Common Compilation Errors (
            {(analysisData.common_patterns?.compilation_errors || []).length})
          </button>
          <div className="accordion-content">
            {(analysisData.common_patterns?.compilation_errors || []).map(
              (pattern) => renderSubmissionPattern(pattern)
            )}
          </div>
        </div>

        <div className="error-section semantic">
          <button
            className="accordion-button"
            onClick={(e) => e.target.parentElement.classList.toggle("active")}
          >
            Common Semantic Errors (
            {(analysisData.common_patterns?.semantic_errors || []).length})
          </button>
          <div className="accordion-content">
            {(analysisData.common_patterns?.semantic_errors || []).map(
              (pattern) => renderSubmissionPattern(pattern)
            )}
          </div>
        </div>

        <div className="error-section correct">
          <button
            className="accordion-button"
            onClick={(e) => e.target.parentElement.classList.toggle("active")}
          >
            Common Correct Solutions (
            {(analysisData.common_patterns?.correct_solutions || []).length})
          </button>
          <div className="accordion-content">
            {(analysisData.common_patterns?.correct_solutions || []).map(
              (pattern) => renderSubmissionPattern(pattern)
            )}
          </div>
        </div>
      </div>

      {/* Teacher Recommendation */}
      <div className="recommendation-container">
        <h3>Recommendation for Teacher</h3>
        <div className="recommendation-card">
          <p>
            {insightData.teacher_recommendation ||
              "Based on the error patterns observed, consider reviewing the most common error types with your students."}
          </p>
        </div>
      </div>
    </div>
  );
}

export default TeacherInsight;
